import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["filterBranch", "debugOptions"];
import { Fragment } from 'react';
import * as React from 'react';
import invariant from 'react-utils/invariant';
import styled from 'styled-components';
import WalmartFilterBranchAssociationLabel from 'flow-doctor-lib/components/filters/WalmartFilterBranchAssociationLabel';
import WalmartFilterBranchOperatorLabel from 'flow-doctor-lib/components/filters/WalmartFilterBranchOperatorLabel';
import WalmartFilterRenderer from 'flow-doctor-lib/components/filters/WalmartFilterRenderer';
import { DebugFilterBranchRenderAdapter, FilterBranchRenderAdapter } from 'flow-doctor-lib/constants/BranchRenderAdapter';
import { groupAssociationFiltersByIndexAndObjectType } from 'flow-doctor-lib/utils/filterRenderUtils';
import UIAccordionItem from 'UIComponents/accordion/UIAccordionItem';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledFilterBranchContainer = styled.div.withConfig({
  displayName: "WalmartFilterBranchRenderer__StyledFilterBranchContainer",
  componentId: "sc-1f1hwsy-0"
})(({
  theme,
  bordered,
  backgroundColor,
  padded
}) => `
  border: ${bordered ? `solid 1px ${theme.color['border-primary-subtle']}` : 'none'};
  border-radius: 3px;
  padding: ${padded ? theme.space['200'] : theme.space['0']};
  background-color: ${backgroundColor || 'initial'};
`);
const WalmartFilterBranchRenderer = ({
  filterBranchAdapter,
  filterWidthType,
  objectTypeId,
  topLevel = false,
  bordered = true,
  fallbackFilterElement,
  padded = true
}) => {
  const isAssociationBranch = filterBranchAdapter.getIsAssociationBranch();
  const associationDisplayMode = filterBranchAdapter.getAssociationDisplayMode();
  const filterBranchesToMap = filterBranchAdapter.getRenderableFilterBranches();
  const filtersToMap = filterBranchAdapter.getRenderableFilters();
  const renderBranch = (innerFilterBranch, index) => {
    const filterBranch = /*#__PURE__*/_jsx(WalmartFilterBranchRenderer, {
      objectTypeId: filterBranchAdapter.getObjectTypeIdForInnerFilterBranch(objectTypeId, innerFilterBranch),
      filterBranchAdapter: innerFilterBranch,
      filterWidthType: filterWidthType,
      bordered: innerFilterBranch.getIsAssociationBranch() || topLevel,
      fallbackFilterElement: fallbackFilterElement
    });
    // If we're at the top level AND there's multiple filter branches being shown
    // wrap the filter branch in an accordion item
    const wrapInAccordionItem = topLevel && filterBranchesToMap.length > 1;
    const content = wrapInAccordionItem ? /*#__PURE__*/_jsx(UIAccordionItem, {
      title: innerFilterBranch.renderDetailsComponent(index + 1),
      defaultOpen: index === 0,
      size: "medium",
      "data-test-id": `filter-branch-${index + 1}`,
      children: filterBranch
    }) : filterBranch;
    return /*#__PURE__*/_jsxs(Fragment, {
      children: [content, (index < filterBranchesToMap.length - 1 || filtersToMap.length > 0) && /*#__PURE__*/_jsx(WalmartFilterBranchOperatorLabel, {
        filterBranchOperator: filterBranchAdapter.getFilterBranchOperator(),
        large: topLevel
      })]
    }, index);
  };
  return /*#__PURE__*/_jsxs(StyledFilterBranchContainer, {
    bordered: bordered,
    backgroundColor: filterBranchAdapter.getBackgroundColor(),
    padded: padded,
    children: [isAssociationBranch && /*#__PURE__*/_jsx(WalmartFilterBranchAssociationLabel, {
      objectTypeId: filterBranchAdapter.getObjectTypeId() // Guaranteed to be present since `isAssociationBranch` is true
      ,
      objectId: filterBranchAdapter.getObjectId(),
      associationDisplayMode: associationDisplayMode
    }), filterBranchesToMap.map(renderBranch), filtersToMap.map((filterAdapter, i) => /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx(WalmartFilterRenderer, {
        filterAdapter: filterAdapter,
        objectTypeId: objectTypeId,
        filterWidthType: filterWidthType,
        fallbackFilterElement: fallbackFilterElement
      }), i < filtersToMap.length - 1 && /*#__PURE__*/_jsx(WalmartFilterBranchOperatorLabel, {
        filterBranchOperator: filterBranchAdapter.getFilterBranchOperator()
      })]
    }, i))]
  });
};
const makeDebugAdapterOptions = (debugDisplayMode, filterBranch) => {
  if (debugDisplayMode !== 'ONLY_ACCEPTED') {
    return {
      displayMode: debugDisplayMode,
      originalAssociationFilterMappings: groupAssociationFiltersByIndexAndObjectType(filterBranch)
    };
  }
  return {
    displayMode: debugDisplayMode
  };
};
const withFilterBranchRendererFacade = WrappedComponent => {
  return props => {
    const {
        filterBranch,
        debugOptions
      } = props,
      restProps = _objectWithoutPropertiesLoose(props, _excluded);
    invariant(!!filterBranch || !!debugOptions, 'WalmartFilterBranchRenderer must be provided either a `filterBranch` or `debugOptions`');
    const debugFilterBranch = debugOptions && debugOptions.filterBranch;
    const debugDisplayMode = debugOptions && debugOptions.displayMode;
    const filterBranchAdapter = React.useMemo(() => {
      if (debugDisplayMode) {
        invariant(debugDisplayMode === 'ONLY_ACCEPTED' || filterBranch, 'FilterBranch must be supplied when `debugDisplayMode` is not `ONLY_ACCEPTED`');
        return new DebugFilterBranchRenderAdapter(debugFilterBranch, makeDebugAdapterOptions(debugDisplayMode, filterBranch));
      }
      return new FilterBranchRenderAdapter(filterBranch);
    }, [filterBranch, debugDisplayMode, debugFilterBranch]);
    return /*#__PURE__*/_jsx(WrappedComponent, Object.assign({}, restProps, {
      filterBranchAdapter: filterBranchAdapter
    }));
  };
};
export default withFilterBranchRendererFacade(WalmartFilterBranchRenderer);